import {
  DefaultTheme,
  getStateFromPath,
  type LinkingOptions,
  NavigationContainer,
} from "@react-navigation/native";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { useTheme } from "tamagui";
import type { PlanTemplateType } from "../../../templates/generated/template_types";
import type { ModuleType } from "../../../templates/types";
import { BusinessAdminScreen } from "../../business/admin/BusinessAdminScreen";
import { BusinessAdminTemplateEditScreen } from "../../business/admin/templates/BusinessAdminTemplateEditScreen";
import { BusinessAdminTemplateScheduleItemEditScreen } from "../../business/admin/templates/BusinessAdminTemplateScheduleItemEditScreen";
import { BusinessAdminTemplateScreen } from "../../business/admin/templates/BusinessAdminTemplateScreen";
import { BusinessAdminTemplateSectionEditScreen } from "../../business/admin/templates/BusinessAdminTemplateSectionEditScreen";
import { BusinessAdminTemplateSectionModuleEditScreen } from "../../business/admin/templates/BusinessAdminTemplateSectionModuleEditScreen";
import { BusinessAdminDevScreen } from "../../business/admindev/BusinessAdminDevScreen";
import BusinessModuleScreen from "../../business/modules/BusinessModuleScreen";
import BusinessPlanDemoScreen from "../../business/plan/BusinessPlanDemoScreen";
import BusinessPlanFeedItemScreen from "../../business/plan/BusinessPlanFeedItemScreen";
import BusinessPlanHomeScreen from "../../business/plan/BusinessPlanHomeScreen";
import BusinessPlanIntroScreen from "../../business/plan/BusinessPlanIntroScreen";
import BusinessPlanMembersScreen from "../../business/plan/BusinessPlanMembersScreen";
import BusinessPlanScheduleScreen from "../../business/plan/BusinessPlanScheduleScreen";
import BusinessPlanSectionModuleScreen from "../../business/plan/BusinessPlanSectionModuleScreen";
import BusinessPlanSectionScreen from "../../business/plan/BusinessPlanSectionScreen";
import { BusinessPlanSettingsScreen } from "../../business/plan/BusinessPlanSettingsScreen";
import BusinessTasksScreen from "../../business/plan/BusinessTasksScreen";
import { BusinessProfileScreen } from "../../business/plan/tabs/BusinessProfileScreen";
import { trackPageView } from "../../core/firebaseInit";
import { CreatePlanTypeScreen } from "../../core/screens/CreatePlanTypeScreen";
import { CreateWizard } from "../../core/screens/create-wizard/CreateWizard";
import { HomeScreen } from "../../core/screens/HomeScreen";
import { LearnScreen } from "../../core/screens/LearnScreen";
import { isRunningInApp } from "../../core/utils";
import { DevPlansScreen } from "../../dev/DevPlansScreen";
import { DevScreen } from "../../dev/DevScreen";
import { ModuleScreen } from "../../modules/core/ModuleScreen";
import { sendMessageToReactNative } from "../../native/webapplogic";
import { MessageType } from "../../native/webviewtypes";
import { ArchiveScreen } from "../../plans/ArchiveScreen";
import { PlanScreen } from "../../plans/PlanScreen";
import { PlanSettingsScreen } from "../../plans/PlanSettingsScreen";
import { TemplateScreen } from "../../templates/TemplateScreen";
import { ProfileScreen } from "../../users/ProfileScreen";
import { UserSelectUI } from "../../users/UserSelectUI";
import { navigationRef } from "./NavigateGlobal";
import { createPlainNavigator } from "./PlainNavigator";

export type RootStackParamList = {
  Home: undefined;
  Plans: undefined;
  Create: { step?: string };
  Template: { templateUid?: string };
  CreatePlanType: { planType: PlanTemplateType };

  // Plan
  Plan: { planUid: string };
  Learn: { planUid: string; card: ModuleType; sectionUid?: string };
  PlanSettings: { planUid: string };
  UserSelect: { planUid: string; moduleUid?: string; dataUid?: string };
  Archive: { planUid: string };
  Profile: { userUid: string; planUid: string };
  Module: { planUid: string; moduleUid: string; dataUid?: string };

  // Devs
  DevAdmin: undefined;

  // Customers
  CustomerAdmin: {
    customerUid: string;
    tab?: string;
  };
  CustomerAdminTemplate: {
    customerUid: string;
    templateUid: string;
    tab?: string;
  };
  CustomerAdminTemplateEdit: {
    customerUid: string;
    templateUid: string;
    moduleUid: string;
  };
  CustomerAdminTemplateScheduleItemEdit: {
    customerUid: string;
    templateUid: string;
    scheduleItemUid: string;
  };
  CustomerAdminTemplateSectionEdit: {
    customerUid: string;
    templateUid: string;
    sectionUid: string;
  };
  CustomerAdminTemplateSectionModuleEdit: {
    customerUid: string;
    templateUid: string;
    sectionUid: string;
    moduleUid: string;
  };

  // Business
  BusinessDemo: undefined;
  BusinessHome: {
    businessUid: string;
  };
  BusinessIntro: {
    businessUid: string;
  };
  BusinessFeedItem: {
    businessUid: string;
    itemUid?: string;
  };
  BusinessSchedule: {
    businessUid: string;
  };
  BusinessScheduleItem: {
    businessUid: string;
    itemUid: string;
  };
  BusinessSection: {
    businessUid: string;
    sectionUid?: string;
  };
  BusinessSectionModule: {
    businessUid: string;
    itemUid: string;
  };
  BusinessTasksModule: { businessUid: string; businessModuleUid: string };
  BusinessPlan: {
    businessUid: string;
  };
  BusinessMembers: {
    businessUid: string;
  };
  BusinessTasks: {
    businessUid: string;
    tab?: "team";
  };
  BusinessTeam: {
    businessUid: string;
  };
  BusinessSettings: {
    businessUid: string;
    tab?: string;
  };
  BusinessTeamModule: {
    planUid: string;
    businessUid: string;
    moduleUid: string;
    dataUid?: string;
  };
  BusinessTeamSettings: {
    planUid: string;
    businessUid: string;
  };
  BusinessProfile: {
    userUid: string;
    businessUid: string;
  };
  BusinessLearn: {
    planUid: string;
    businessUid: string;
    card: ModuleType;
    sectionUid?: string;
  };

  // Dev
  Dev: undefined;
  DevPlans: undefined;
};

declare global {
  namespace ReactNavigation {
    interface RootParamList extends RootStackParamList {}
  }
}

const linking: LinkingOptions<RootStackParamList> = {
  prefixes: ["/plan"],
  config: {
    screens: {
      Home: "plan/",
      Dev: "plan/dev",
      DevPlans: "plan/devViewer",

      // Create pages
      Create: "plan/create/:step?",
      Template: "plan/template/:templateUid?",
      CreatePlanType: "plan/create/:planType",

      // Plan pages
      Learn: "plan/:planUid/learn/:card/:sectionUid?",
      Plan: "plan/:planUid",
      PlanSettings: "plan/:planUid/settings",
      UserSelect: "plan/:planUid/user-select",
      Profile: "plan/:planUid/profile/userUid",
      Module: "plan/:planUid/:moduleUid/:dataUid?",
      Archive: "plan/:planUid/archive",

      // Devs
      DevAdmin: "business/dev",

      // Customers
      CustomerAdmin: "business/:customerUid/:tab?",
      CustomerAdminTemplate: "business/t/:customerUid/:templateUid/:tab?",
      CustomerAdminTemplateEdit:
        "business/t/:customerUid/:templateUid/:moduleUid",
      CustomerAdminTemplateScheduleItemEdit:
        "business/c/:customerUid/:templateUid/:scheduleItemUid",
      CustomerAdminTemplateSectionEdit:
        "business/s/:customerUid/:templateUid/:sectionUid",
      CustomerAdminTemplateSectionModuleEdit:
        "business/s/:customerUid/:templateUid/:sectionUid/:moduleUid",

      // Business
      BusinessDemo: "event/demo",
      BusinessHome: "event/:businessUid",
      BusinessIntro: "event/:businessUid/welcome",
      BusinessMembers: "event/:businessUid/members",
      BusinessProfile: "event/:businessUid/u/:userUid",
      BusinessSettings: "event/:businessUid/settings/:tab?",
      BusinessFeedItem: "event/:businessUid/item/:itemUid?",
      BusinessSchedule: "event/:businessUid/calendar",
      BusinessScheduleItem: "event/:businessUid/calendar/:itemUid",
      BusinessSection: "event/:businessUid/s/:sectionUid?",
      BusinessSectionModule: "event/:businessUid/i/:itemUid",
      BusinessTasks: "event/:businessUid/tasks/:tab?",
      BusinessTasksModule: "event/:businessUid/tasks/m/:businessModuleUid",
      BusinessTeamSettings: "event/:businessUid/team/:planUid/settings",
      BusinessTeamModule:
        "event/:businessUid/team/c/:planUid/:moduleUid/:dataUid?",
      BusinessLearn:
        "event/:businessUid/team/learn/:planUid/:card/:sectionUid?",
    },
  },
};

const PlainNavigator = createPlainNavigator();

type ScrollPositions = Record<string, number>;

export const matchRoute = (path: string) => {
  const state = getStateFromPath(path, linking.config);
  if (!state || !state.routes.length) return null;

  return state.routes[state.routes.length - 1]; // Get the last route in the state
};

export const Navigator = observer(() => {
  const theme = useTheme();
  const routeNameRef = useRef<string>("");
  const routeKeyRef = useRef<string | null>(null); // To store the current route key
  const scrollPositions = useRef<ScrollPositions>({}); // To store scroll positions for each route
  const routeStackSizeRef = useRef<number>(0);

  useEffect(() => {
    if (isRunningInApp()) {
      sendMessageToReactNative({ type: MessageType.WebReady });
    }
  }, []);

  return (
    <NavigationContainer
      ref={navigationRef}
      onReady={() => {
        if (navigationRef) {
          routeNameRef.current = navigationRef.getCurrentRoute()?.name ?? "";
          routeKeyRef.current = navigationRef.getCurrentRoute()?.key ?? "";
        }

        sendMessageToReactNative({
          type: MessageType.CanGoBack,
          canGoBack: navigationRef.canGoBack() ?? false,
        });
      }}
      onStateChange={async (state) => {
        const currentRoute = navigationRef.getCurrentRoute();
        const currentRouteName = currentRoute?.name ?? "";
        const currentRouteKey = currentRoute?.key ?? "";
        const previousStackSize = routeStackSizeRef.current;
        const currentStackSize = state?.routes.length ?? 0;

        // Update the route stack size reference
        routeStackSizeRef.current = currentStackSize;

        // Save the scroll position of the current route before navigating
        const previousRouteKey = routeKeyRef.current;
        if (previousRouteKey) {
          scrollPositions.current[previousRouteKey] = window.scrollY;
        }

        // Update the current route key
        routeKeyRef.current = currentRouteKey;

        // Detect navigation type
        const isBackNavigation = currentStackSize < previousStackSize;

        const newScrollPosition =
          (isBackNavigation && scrollPositions.current[currentRouteKey]) || 0;

        requestAnimationFrame(() => {
          window.scrollTo({ top: newScrollPosition, behavior: "instant" });
        });

        const previousRouteName = routeNameRef.current;
        if (previousRouteName !== currentRouteName) {
          // Save the current route name for later comparison
          routeNameRef.current = currentRouteName;

          trackPageView(currentRouteName);

          sendMessageToReactNative({
            type: MessageType.CanGoBack,
            canGoBack: navigationRef.canGoBack() ?? false,
          });
        }
      }}
      theme={{
        ...DefaultTheme,
        colors: {
          primary: theme.primary.val,
          card: theme.backgroundBlock.val,
          text: theme.color.val,
          border: theme.divider.val,
          notification: theme.backgroundBlock.val,
          background: theme.background.val,
        },
      }}
      linking={linking}
    >
      <PlainNavigator.Navigator
        screenOptions={{
          header: () => null,
        }}
      >
        <PlainNavigator.Screen
          name="Home"
          component={HomeScreen}
          options={{ title: "Planira" }}
        />
        <PlainNavigator.Screen
          name="Create"
          component={CreateWizard}
          options={{ title: "Planira" }}
        />
        <PlainNavigator.Screen
          name="Template"
          component={TemplateScreen}
          options={{ title: "Planira" }}
        />
        <PlainNavigator.Screen
          name="CreatePlanType"
          component={CreatePlanTypeScreen}
          options={{ title: "Start a new Planira" }}
        />
        <PlainNavigator.Screen
          name="Learn"
          component={LearnScreen}
          options={{ title: "Learn" }}
        />
        <PlainNavigator.Screen
          name="Plan"
          component={PlanScreen}
          options={{ title: "Planira" }}
        />
        <PlainNavigator.Screen
          name="PlanSettings"
          component={PlanSettingsScreen}
          options={{ title: "Planira" }}
        />
        <PlainNavigator.Screen
          name="UserSelect"
          component={UserSelectUI}
          options={{ title: "Planira" }}
        />
        <PlainNavigator.Screen
          name="Module"
          component={ModuleScreen}
          options={{ title: "Planira" }}
        />
        <PlainNavigator.Screen
          name="Profile"
          component={ProfileScreen}
          options={{ title: "Profile - Planira" }}
        />
        <PlainNavigator.Screen
          name="Archive"
          component={ArchiveScreen}
          options={{ title: "Archive - Planira" }}
        />
        <PlainNavigator.Screen name="Dev" component={DevScreen} />
        <PlainNavigator.Screen
          name="DevPlans"
          component={DevPlansScreen}
          options={{ title: "Planira" }}
        />

        {/* DEVS */}
        <PlainNavigator.Screen
          name="DevAdmin"
          component={BusinessAdminDevScreen}
          options={{ title: "DevAdmin" }}
        />

        {/* CUSTOMERS */}
        <PlainNavigator.Screen
          name="CustomerAdmin"
          component={BusinessAdminScreen}
          options={{ title: "CustomerAdmin" }}
        />
        <PlainNavigator.Screen
          name="CustomerAdminTemplate"
          component={BusinessAdminTemplateScreen}
          options={{ title: "Template" }}
        />
        <PlainNavigator.Screen
          name="CustomerAdminTemplateEdit"
          component={BusinessAdminTemplateEditScreen}
          options={{ title: "Template - Edit" }}
        />
        <PlainNavigator.Screen
          name="CustomerAdminTemplateSectionEdit"
          component={BusinessAdminTemplateSectionEditScreen}
          options={{ title: "Section - Edit" }}
        />
        <PlainNavigator.Screen
          name="CustomerAdminTemplateScheduleItemEdit"
          component={BusinessAdminTemplateScheduleItemEditScreen}
          options={{ title: "Schedule - Edit" }}
        />

        <PlainNavigator.Screen
          name="CustomerAdminTemplateSectionModuleEdit"
          component={BusinessAdminTemplateSectionModuleEditScreen}
          options={{ title: "Section - Edit" }}
        />

        {/* BUSINESS */}
        <PlainNavigator.Screen
          name="BusinessDemo"
          component={BusinessPlanDemoScreen}
          options={{ title: "Demo time" }}
        />
        <PlainNavigator.Screen
          name="BusinessHome"
          component={BusinessPlanHomeScreen}
          options={{ title: "Business" }}
        />
        <PlainNavigator.Screen
          name="BusinessIntro"
          component={BusinessPlanIntroScreen}
          options={{ title: "Welcome" }}
        />
        <PlainNavigator.Screen
          name="BusinessMembers"
          component={BusinessPlanMembersScreen}
          options={{ title: "Members" }}
        />
        <PlainNavigator.Screen
          name="BusinessProfile"
          component={BusinessProfileScreen}
          options={{ title: "Profile" }}
        />
        <PlainNavigator.Screen
          name="BusinessFeedItem"
          component={BusinessPlanFeedItemScreen}
          options={{ title: "Item" }}
        />
        <PlainNavigator.Screen
          name="BusinessSchedule"
          component={BusinessPlanScheduleScreen}
          options={{ title: "Calendar" }}
        />
        <PlainNavigator.Screen
          name="BusinessScheduleItem"
          component={BusinessPlanScheduleScreen}
          options={{ title: "Calendar" }}
        />
        <PlainNavigator.Screen
          name="BusinessTasksModule"
          component={BusinessModuleScreen}
          options={{ title: "Module" }}
        />
        <PlainNavigator.Screen
          name="BusinessSection"
          component={BusinessPlanSectionScreen}
          options={{ title: "Section" }}
        />
        <PlainNavigator.Screen
          name="BusinessSectionModule"
          component={BusinessPlanSectionModuleScreen}
          options={{ title: "Section Module" }}
        />
        <PlainNavigator.Screen
          name="BusinessSettings"
          component={BusinessPlanSettingsScreen}
          options={{ title: "Settings" }}
        />
        <PlainNavigator.Screen
          name="BusinessTasks"
          component={BusinessTasksScreen}
          options={{ title: "Tasks" }}
        />
        <PlainNavigator.Screen
          name="BusinessTeamSettings"
          component={PlanSettingsScreen}
          options={{ title: "Planira" }}
        />
        <PlainNavigator.Screen
          name="BusinessLearn"
          component={LearnScreen}
          options={{ title: "Planira" }}
        />
        <PlainNavigator.Screen
          name="BusinessTeamModule"
          component={ModuleScreen}
          options={{ title: "Team Module" }}
        />
      </PlainNavigator.Navigator>
    </NavigationContainer>
  );
});
